import {
  getMultiStepScenarioPerformanceCollection,
  setMultiStepScenarioPerformanceCollection,
} from '@ms/yammer-telemetry-store';
import type { MultiStepScenarioPerformanceData } from '@ms/yammer-telemetry-support';

import { endAndReportPerformanceMeasure, startPerformanceMeasure } from './performance';

const fiveMinutesInMS = 300_000;

type ClearScenario = (scenarioData: MultiStepScenarioPerformanceData) => void;
const clearScenario: ClearScenario = ({ key, timer }) => {
  window.clearTimeout(timer);

  const scenarioCollection = getMultiStepScenarioPerformanceCollection();

  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete scenarioCollection[key];

  setMultiStepScenarioPerformanceCollection(scenarioCollection);
};

const createScenarioKey = (scenario: string) => `${scenario}-${performance.now()}`;

const getScenarioData = (key: string) => getMultiStepScenarioPerformanceCollection()[key];

type StartStep = (step: number, name: string) => void;
type EndStep = (step: number) => void;
interface EndScenarioOptions {
  readonly endedWithError?: boolean;
  readonly timedOut?: boolean;
  readonly interrupted?: boolean;
}
type EndScenario = (options?: EndScenarioOptions) => void;

export interface MultiStepScenarioInstance {
  readonly startStep: StartStep;
  readonly endStep: EndStep;
  readonly endAndReport: EndScenario;
}

export interface StartMultiStepScenarioPerformanceMeasureOptions {
  /**
   * The name of the scenario being measured
   */
  readonly scenario: string;

  /**
   * An optional timeout in milliseconds
   * @default 300_000 (5 minutes)
   */
  readonly timeoutInMS?: number;
}
type StartMultiStepScenarioPerformanceMeasure = (
  options: StartMultiStepScenarioPerformanceMeasureOptions
) => MultiStepScenarioInstance;
export const startMultiStepScenarioPerformanceMeasure: StartMultiStepScenarioPerformanceMeasure = ({
  scenario,
  timeoutInMS,
}) => {
  const scenarioCollection = getMultiStepScenarioPerformanceCollection();
  const key = createScenarioKey(scenario);

  const scenarioData: MultiStepScenarioPerformanceData = {
    measure: startPerformanceMeasure('multi_step_scenario', key),
    scenario,
    key,
    steps: [],
    timer: window.setTimeout(() => {
      endAndReportMultiStepScenarioPerformanceMeasure({
        key,
        timedOut: true,
      });
    }, timeoutInMS || fiveMinutesInMS),
  };
  scenarioCollection[key] = scenarioData;

  setMultiStepScenarioPerformanceCollection(scenarioCollection);

  return {
    startStep: (step, name) => startMultiStepScenarioStep(key, step, name),
    endStep: (step) => endMultiStepScenarioStep(key, step),
    endAndReport: (options = {}) => endAndReportMultiStepScenarioPerformanceMeasure({ key, ...options }),
  };
};

interface EndAndReportScenarioPerformanceOptions extends EndScenarioOptions {
  readonly key: string;
}

type EndAndReportScenarioPerformanceMeasure = (options: EndAndReportScenarioPerformanceOptions) => void;

const endAndReportMultiStepScenarioPerformanceMeasure: EndAndReportScenarioPerformanceMeasure = ({
  key,
  endedWithError = false,
  timedOut = false,
  interrupted = false,
}) => {
  const scenarioData = getScenarioData(key);

  if (!scenarioData) {
    return;
  }

  endAndReportPerformanceMeasure({
    ...scenarioData.measure,
    eventProperties: {
      ...scenarioData.measure.eventProperties,
      endedWithError,
      timedOut,
      interrupted,
      scenario: scenarioData.scenario,
      payload: JSON.stringify({ steps: scenarioData.steps.filter((step) => !!step.duration) }),
    },
  });
  clearScenario(scenarioData);
};

type StartMultiStepScenarioStep = (key: string, step: number, name: string) => void;
const startMultiStepScenarioStep: StartMultiStepScenarioStep = (key, step, name) => {
  const scenarioData = getScenarioData(key);

  // istanbul ignore next
  if (!scenarioData) {
    return;
  }

  const isDuplicateStep = scenarioData.steps.some((item) => item.step === step);
  if (isDuplicateStep) {
    throw new Error(`Step "${step}" already exists in multi step scenario ${scenarioData.scenario}`);
  }

  scenarioData.steps.push({ step, name, startTime: performance.now() });
  const scenarioCollection = getMultiStepScenarioPerformanceCollection();
  scenarioCollection[key] = scenarioData;
  setMultiStepScenarioPerformanceCollection(scenarioCollection);
};

type EndMultiStepScenarioStep = (key: string, step: number) => void;
const endMultiStepScenarioStep: EndMultiStepScenarioStep = (key, step) => {
  const scenarioData = getScenarioData(key);

  // istanbul ignore next
  if (!scenarioData) {
    return;
  }

  const index = scenarioData.steps.findIndex((item) => item.step === step);
  if (index < 0) {
    return;
  }
  const stepItem = scenarioData.steps[index];

  const isAlreadyEnded = !!stepItem.endTime;
  if (isAlreadyEnded) {
    return;
  }

  const endTime = performance.now();
  const duration = endTime - stepItem.startTime;

  scenarioData.steps[index] = { ...stepItem, endTime, duration };
  const scenarioCollection = getMultiStepScenarioPerformanceCollection();
  scenarioCollection[key] = scenarioData;
  setMultiStepScenarioPerformanceCollection(scenarioCollection);
};
